import type { SceneElement } from '@easterngraphics/wcf/modules/core/mdl';
import type { EaiwsContext} from '@easterngraphics/wcf/modules/eaiws';
import type App from '@egr/xbox/base-app/App';

import * as React from 'react';

import { ArticleItem, FolderItem } from '@easterngraphics/wcf/modules/bsk';
import { ArticleElement, MainArticleElement } from '@easterngraphics/wcf/modules/cf';
import { NodeUserData } from '@easterngraphics/wcf/modules/core/mdl';
import { ProjectContext } from '@easterngraphics/wcf/modules/eaiws';
import { DeleteItemsOptions, ItemSelectionOptions, type BasketService } from '@easterngraphics/wcf/modules/eaiws/basket';
import { SaveSessionOptions } from '@easterngraphics/wcf/modules/eaiws/session';
import { ListenableEvent } from '@easterngraphics/wcf/modules/utils/ListenableEvent';
import { isNotNullOrEmpty } from '@easterngraphics/wcf/modules/utils/string';

const DEPRECATED_ARTICLEELEMENT_NODEDATA_SOURCE_CATALOG = 'pConCatalog';
const ARTICLEELEMENT_NODEDATA_SOURCE = 'pCon:source';
const ARTICLEELEMENT_NODEDATA_SOURCE_PCONCATALOG_VALUE = 'P-CAT';

interface SourceUpdateArg {
    id: string | null;
    value: string | null;
}

export function *getInvalidForArticleListElements(elements: ReadonlyArray<SceneElement>): IterableIterator<ArticleElement> {
    for (const element of elements) {
        if (
            isPconCatalogModel(element)
        ) {
            yield element;
        }
    }
}

export function *getOfmlArticles(elements: ReadonlyArray<SceneElement>): IterableIterator<MainArticleElement> {
    for (const element of elements) {
        if (
            element instanceof MainArticleElement &&
            isOFMLArticle(element)
        ) {
            yield element;
        }
    }
}

export async function *getValideExportModels(app: App): AsyncIterableIterator<ArticleElement | ArticleItem | FolderItem> {
    const ids: Array<string> = [];
    for (const element of app.coreApp.model.elements) {
        if (
            element instanceof ArticleElement &&
            isNotNullOrEmpty(element.basketId) &&
            !isPconCatalogModel(element)
        ) {
            yield element;
            ids.push(element.basketId);
        }
    }

    for (const basketItem of app.eaiwsBaskets.basket.root.children) {
        if (ids.includes(basketItem.id)) {
            continue;
        }

        if (
            basketItem instanceof ArticleItem ||
            basketItem instanceof FolderItem
        ) {
            const source = (await app.eaiwsBaskets.session.basket.getItemAppData(basketItem.id, 'pCon', ['source']))?.[0];
            if (source === ARTICLEELEMENT_NODEDATA_SOURCE_PCONCATALOG_VALUE) {
                continue;
            }

            const sourceDeprecated = (await app.eaiwsBaskets.session.basket.getItemAppData(basketItem.id, 'planner', ['source']))?.[0];
            if (isNotNullOrEmpty(sourceDeprecated)) {
                // remove deprecated value
                await app.eaiwsBaskets.session.basket.setItemAppData(basketItem.id, 'planner:source', ['']);
            }

            if (
                sourceDeprecated === DEPRECATED_ARTICLEELEMENT_NODEDATA_SOURCE_CATALOG
            ) {
                await app.eaiwsBaskets.session.basket.setItemAppData(basketItem.id, ARTICLEELEMENT_NODEDATA_SOURCE, ['P-CAT']);
                continue;
            }

            yield basketItem;
            ids.push(basketItem.id);
        }
    }
}

export function isPconCatalogModel(element: SceneElement | undefined | null): element is (ArticleElement & { basketId: string; }) {
    return element instanceof ArticleElement && NodeUserData.GetData<string>(element.node, ARTICLEELEMENT_NODEDATA_SOURCE) === ARTICLEELEMENT_NODEDATA_SOURCE_PCONCATALOG_VALUE;
}

export function isPconCatalogArticle(element: SceneElement | undefined | null): element is (MainArticleElement & { basketId: string; }) {
    return element instanceof MainArticleElement && NodeUserData.GetData<string>(element.node, ARTICLEELEMENT_NODEDATA_SOURCE) === ARTICLEELEMENT_NODEDATA_SOURCE_PCONCATALOG_VALUE;
}

export const EventSourceChanged = new ListenableEvent<SourceUpdateArg, undefined>(undefined);

export function usePconCatalogModel(element: SceneElement | null | undefined): boolean {
    const [ state, setState ] = React.useState(isPconCatalogModel(element));

    React.useEffect(
        () => {
            const updateState = ({ id, value }: SourceUpdateArg) => {
                if (element instanceof ArticleElement && id === element.basketId) {
                    setState(value === ARTICLEELEMENT_NODEDATA_SOURCE_PCONCATALOG_VALUE);
                }
            };
            setState(isPconCatalogModel(element));
            EventSourceChanged.addListener(updateState);
            return () => EventSourceChanged.removeListener(updateState);
        },
        [ element ]
    );

    return state;
}

function isOFMLArticle(element: SceneElement): element is (MainArticleElement & { basketId: string; }) {
    return !isPconCatalogModel(element) && element instanceof MainArticleElement;
}

function setSourceFlag(element: ArticleElement, value: 'P-CAT' | 'P-CAT:B2B' | null): void {
    NodeUserData.SetData(
        element.node,
        ARTICLEELEMENT_NODEDATA_SOURCE,
        value
    );
    EventSourceChanged.trigger({ id: element.basketId, value });
}

export async function setSourceFlagPersistent(
    elements: Array<SceneElement> | ReadonlyArray<SceneElement>,
    source: 'P-CAT' | 'P-CAT:B2B' | null,
    basket: BasketService
): Promise<void> {
    await Promise.all((elements as Array<SceneElement>).reduce(
        (previous: Array<Promise<void>>, current: SceneElement): Array<Promise<void>> => {
            if (current instanceof MainArticleElement && isNotNullOrEmpty(current.basketId)) {
                const element = current as MainArticleElement & { basketId: string; };
                async function task() {
                    await basket.setItemAppData(element.basketId, 'pCon', [`source=${source ?? ''}`]);
                    setSourceFlag(element, source);
                }
                return [...previous, task()];
            }
            return previous;
        },
        []
    ));
}
export async function updateSourceFlagsAfterImport(elements: ReadonlyArray<SceneElement>, basket: BasketService): Promise<void> {
    const idsForFix = (await Promise.all(elements.map((value) => updateSourceFlag(value, basket)))).filter(isNotNullOrEmpty);

    if (idsForFix.length) {
        const options = new ItemSelectionOptions();
        options.wholeComposite = true;
        options.subItems = true;
        await basket.changeAlternativePositionState(idsForFix, '', false, options);
    }
}

/**
 * @param element
 * @param basket
 * @returns basketId of element if it has wrong alternativePosition setting because of import of pbox-file created in planner web 1.0
 */
export async function updateSourceFlag(element: SceneElement, basket: BasketService): Promise<string | null> {
    if (element instanceof MainArticleElement && isNotNullOrEmpty(element.basketId)) {
        const cacheValue = NodeUserData.GetData<string>(element.node, ARTICLEELEMENT_NODEDATA_SOURCE);

        if (isNotNullOrEmpty(cacheValue)) {
            return null;
        }

        const source = (await basket.getItemAppData(element.basketId, 'pCon', ['source']))?.[0];
        if (source === ARTICLEELEMENT_NODEDATA_SOURCE_PCONCATALOG_VALUE) {
            setSourceFlag(element, 'P-CAT');
            return null;
        }

        // migration
        const sourceDeprecated = (await basket.getItemAppData(element.basketId, 'planner', ['source']))?.[0];
        if (isNotNullOrEmpty(sourceDeprecated)) {
            // remove deprecated value
            await basket.setItemAppData(element.basketId, 'planner', ['source=']);
        }

        if (sourceDeprecated === DEPRECATED_ARTICLEELEMENT_NODEDATA_SOURCE_CATALOG) {
            await setSourceFlagPersistent([element], 'P-CAT', basket);
            return element.basketId;
        }

        setSourceFlag(element, null);
        return null;
    }

    return null;
}

export async function getBasketIds(elements: Array<SceneElement>): Promise<Array<string>> {
    const result: Array<string> = [];

    for (const element of elements) {
        if (element instanceof MainArticleElement) {
            if (element.basketId == null) {
                await element.ensureIsInstanced();
            }
            if (isNotNullOrEmpty(element.basketId)) {
                result.push(element.basketId);
            }
        }
    }

    return result;
}

export function onlyOFMLElements(selection: ReadonlyArray<SceneElement>): boolean {
    for (const element of selection) {
        if (
            element instanceof ArticleElement &&
            isPconCatalogModel(element)
        ) {
            return false;
        }
    }
    return true;
}

interface SplitArticlesReturnValue<T> {
    external: Array<T>;
    internal: Array<T>;
}

export function splitArticles<ElementType extends SceneElement>(before: SplitArticlesReturnValue<ElementType>, element: ElementType): SplitArticlesReturnValue<ElementType> {
    const { external, internal } = before;
    if (isPconCatalogArticle(element)) {
        external.push(element);
    } else {
        internal.push(element);
    }
    return { external, internal };
}

export async function getContextWithValidArticles(app: App): Promise<EaiwsContext> {
    const invalidElementIds = await getBasketIds(Array.from(getInvalidForArticleListElements(app.coreApp.model.elements)));

    await app.eaiwsBaskets.basket.articleManager?.synchronizeSession(false);
    await app.eaiwsBaskets.basket.reloadItems();

    if (invalidElementIds.length === 0) {
        return app.eaiwsBaskets.session;
    }

    const deleteOptions = new DeleteItemsOptions();
    deleteOptions.subItems = true;
    deleteOptions.wholeComposite = true;

    const saveOptions = new SaveSessionOptions();
    saveOptions.export = true;

    if (app.eaiwsBaskets.basket.calculation.activePricingProcedure != null) {
        saveOptions.saveLegacyCalculation = true;
        saveOptions.legacyCalculationName = app.eaiwsBaskets.basket.calculation.activePricingProcedure;
    }

    const obkUrl = await app.eaiwsBaskets.session.session.saveSession(null, saveOptions);

    const alternativeSessionContext = await ProjectContext.Load(app.eaiwsBaskets.session, obkUrl);

    if (invalidElementIds.length !== 0) {
        await alternativeSessionContext.basket.deleteItems(invalidElementIds, deleteOptions);
    }

    return alternativeSessionContext;
}